import { useEffect, useMemo, useState } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import { Alert, Box, Card, CardContent, Typography } from '@mui/material';
import FilterBAndWIcon from '@mui/icons-material/FilterBAndW';
import PaletteIcon from '@mui/icons-material/Palette';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import CustomDataGrid from '../../../../../components/ui/customDataGrid/customDataGrid.component';
import CustomCircularProgress from '../../../../../components/ui/customCircularProgress/customCircularProgress.component';
import { useCRUD } from '../../../../../api/crud';
import { useListCount } from '../../../../../hooks/useListCount';
import { EQUIPMENT_COUNTERS_ENDPOINT, EQUIPMENTS_ENDPOINT } from '../../../../../consts';
import { useHasPermission } from '../../../../../contexts/auth';
import { formatCounterProvider, formatCounterValue, formatDateTime } from '../../../../../utils';
import {
  dateRangeFilterOperators,
  equalsNumberFilterOperators,
} from '../../../../../components/ui/customDataGrid/filterOperators';

const DEFAULT_PAGE_SIZE = 10;

export const EquipmentCounters = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const canViewCounters = useHasPermission("settlement.view_equipmentcounter");
  const { get: getEquipment } = useCRUD(EQUIPMENTS_ENDPOINT);
  // undefined = not loaded yet, null = loaded but kind unknown (e.g. fetch failed)
  const [equipmentKind, setEquipmentKind] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;

    const fetchEquipment = async () => {
      if (!id) {
        return;
      }
      const { data } = await getEquipment(id);
      if (!cancelled) {
        setEquipmentKind(data?.kind ?? null);
      }
    };

    fetchEquipment();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const blackPagesCount = useListCount(
    id ? `${EQUIPMENT_COUNTERS_ENDPOINT}?equipment=${id}&kind=BP` : null,
    canViewCounters
  );
  // Color pages only ever make sense for color-capable equipment - a black-only
  // device can't produce a color counter reading, so don't even bother asking.
  const colorPagesCount = useListCount(
    id && equipmentKind === "C" ? `${EQUIPMENT_COUNTERS_ENDPOINT}?equipment=${id}&kind=CP` : null,
    canViewCounters && equipmentKind === "C"
  );
  const scannedPagesCount = useListCount(
    id ? `${EQUIPMENT_COUNTERS_ENDPOINT}?equipment=${id}&kind=SP` : null,
    canViewCounters
  );

  const handleRowClick = (counterId: string) => {
    navigate(`/equipment-section/equipments/${id}/counters/${counterId}`);
  };

  const columns: GridColDef[] = useMemo(() => [
    {
      field: "id",
      headerName: "ID",
      filterOperators: equalsNumberFilterOperators(),
    },
    {
      field: "provider",
      headerName: t("Provider"),
      flex: 1,
      filterable: false,
      valueGetter: (value, row) =>
        formatCounterProvider(row.provider_display, row.email_source_display),
    },
    {
      field: "value",
      headerName: t("Value"),
      type: "number",
      filterOperators: equalsNumberFilterOperators(),
      valueFormatter: (value) => formatCounterValue(value),
    },
    {
      field: "read_at",
      headerName: t("Read at"),
      filterOperators: dateRangeFilterOperators(),
      valueFormatter: (value) => formatDateTime(value) || "-",
    },
  ], [t]);

  if (!canViewCounters) {
    return null;
  }

  const isLoading =
    equipmentKind === undefined
    || blackPagesCount === undefined
    || scannedPagesCount === undefined
    || (equipmentKind === "C" && colorPagesCount === undefined);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CustomCircularProgress />
      </Box>
    );
  }

  const sections = [
    {
      key: "black",
      kind: "BP",
      label: t("Black pages"),
      icon: <FilterBAndWIcon fontSize="small" />,
      show: (blackPagesCount ?? 0) > 0,
    },
    {
      key: "color",
      kind: "CP",
      label: t("Color pages"),
      icon: <PaletteIcon fontSize="small" />,
      show: equipmentKind === "C" && (colorPagesCount ?? 0) > 0,
    },
    {
      key: "scanned",
      kind: "SP",
      label: t("Scanned pages"),
      icon: <DocumentScannerIcon fontSize="small" />,
      show: (scannedPagesCount ?? 0) > 0,
    },
  ].filter((section) => section.show);

  if (sections.length === 0) {
    return (
      <Box sx={{ p: 1 }}>
        <Alert severity="info">{t("No counters recorded for this equipment yet.")}</Alert>
      </Box>
    );
  }

  // A single visible table gets the full view, like any other data grid in
  // the app, instead of being boxed into a half-width card.
  if (sections.length === 1) {
    const [section] = sections;
    return (
      <Box
        sx={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CustomDataGrid
          icon={section.icon}
          title={section.label}
          url={`${EQUIPMENT_COUNTERS_ENDPOINT}?equipment=${id}&kind=${section.kind}`}
          columns={columns}
          refreshEventName="equipmentCounterAdded"
          onRowClick={handleRowClick}
          defaultPageSize={DEFAULT_PAGE_SIZE}
          dataGridProps={{ pageSizeOptions: [10, 25, 50] }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" },
        gap: 2,
        p: 1,
      }}
    >
      {sections.map((section) => (
        <Card key={section.key}>
          <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography
              variant="h6"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              {section.icon}
              {section.label}
            </Typography>
            <CustomDataGrid
              url={`${EQUIPMENT_COUNTERS_ENDPOINT}?equipment=${id}&kind=${section.kind}`}
              columns={columns}
              refreshEventName="equipmentCounterAdded"
              onRowClick={handleRowClick}
              defaultPageSize={DEFAULT_PAGE_SIZE}
              dataGridProps={{ autoHeight: true, pageSizeOptions: [10, 25, 50] }}
            />
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};
